import { RefObject, useCallback, useEffect, useRef, useState } from "react";

interface StickySwimLaneHeightResult {
  /** Current height of the sticky swimlane content (0 when not sticky). */
  stickySwimLaneHeight: number;
  /** Whether the swimlane is currently in sticky mode. */
  isSwimLaneSticky: boolean;
  /** Ref to attach to the swimlane content wrapper for height measurement. */
  swimLaneStickyContentRef: RefObject<HTMLDivElement | null>;
  /** Callback for StickyScroll's onStickyChange prop. */
  handleSwimLaneStickyChange: (sticky: boolean) => void;
}

/**
 * Tracks the height of a sticky swimlane header via ResizeObserver.
 * Returns 0 when the swimlane is not sticky.
 */
export function useStickySwimLaneHeight(): StickySwimLaneHeightResult {
  const [stickySwimLaneHeight, setStickySwimLaneHeight] = useState(0);
  const [isSwimLaneSticky, setIsSwimLaneSticky] = useState(false);
  const swimLaneStickyContentRef = useRef<HTMLDivElement | null>(null);

  const handleSwimLaneStickyChange = useCallback((sticky: boolean) => {
    setIsSwimLaneSticky(sticky);
    if (!sticky) {
      setStickySwimLaneHeight(0);
    }
  }, []);

  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    const el = swimLaneStickyContentRef.current;
    if (!isSwimLaneSticky || !el) {
      return;
    }
    const observer = new ResizeObserver(() => {
      setStickySwimLaneHeight(el.getBoundingClientRect().height);
    });
    observer.observe(el);
    setStickySwimLaneHeight(el.getBoundingClientRect().height);
    return () => observer.disconnect();
  }, [isSwimLaneSticky]);

  return {
    stickySwimLaneHeight,
    isSwimLaneSticky,
    swimLaneStickyContentRef,
    handleSwimLaneStickyChange,
  };
}
